// src/components/ProtectedRoute/Admin/Dialogs/DialogCountryEditor.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Autocomplete,
  Stack,
} from "@mui/material";
import supabase from "../../../../utils/supabase";

const statusOptions = ["canonical", "synonym", "historical", "pending"];

export default function DialogCountryEditor({ open, onClose, country = null }) {
  const isEdit = !!country;
  const [name, setName] = useState("");
  const [status, setStatus] = useState("pending");
  const [iso, setIso] = useState("");
  const [parentId, setParentId] = useState("");
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [note, setNote] = useState("");
  const [countries, setCountries] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchParents = async () => {
      const { data, error } = await supabase.from("countries").select("id,name,status").order("name");
      if (!error) setCountries(data || []);
    };
    fetchParents();
  }, []);

  useEffect(() => {
    if (!isEdit) {
      setName("");
      setStatus("pending");
      setIso("");
      setParentId("");
      setPeriodFrom("");
      setPeriodTo("");
      setNote("");
      return;
    }
    setName(country?.name || "");
    setStatus(country?.status || "pending");
    setIso(country?.iso_a3 || "");
    setParentId(country?.parent_id || "");
    setPeriodFrom(country?.period_from || "");
    setPeriodTo(country?.period_to || "");
    setNote(country?.note || "");
  }, [isEdit, country]);

  const parentOptions = useMemo(
    () => countries.filter((c) => c.id !== country?.id),
    [countries, country?.id]
  );

  const handleSave = async () => {
    if (!name.trim()) {
      alert("Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        status: status || "pending",
        iso_a3: iso || null,
        parent_id: parentId || null,
        period_from: periodFrom || null,
        period_to: periodTo || null,
        note: note?.trim() || null,
      };
      if (isEdit) {
        const { error } = await supabase.from("countries").update(payload).eq("id", country.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("countries").insert([payload]);
        if (error) throw error;
      }
      onClose?.(true);
    } catch (e) {
      console.error(e);
      alert(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose?.(false)} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? "Edit Country" : "Add Country"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name *" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
          <TextField
            select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            fullWidth
          >
            {statusOptions.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            options={parentOptions}
            getOptionLabel={(o) => o.name || ""}
            value={parentOptions.find((c) => c.id === parentId) || null}
            onChange={(_, v) => setParentId(v ? v.id : "")}
            renderInput={(params) => <TextField {...params} label="Parent (canonical)" />}
          />
          <TextField
            label="ISO A3"
            value={iso}
            onChange={(e) => setIso(e.target.value.toUpperCase())}
            inputProps={{ maxLength: 3 }}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Period From (YYYY-MM-DD)"
              value={periodFrom}
              onChange={(e) => setPeriodFrom(e.target.value)}
              fullWidth
            />
            <TextField
              label="Period To (YYYY-MM-DD)"
              value={periodTo}
              onChange={(e) => setPeriodTo(e.target.value)}
              fullWidth
            />
          </Stack>
          <TextField
            label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose?.(false)} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
